import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  useToast,
  type SelectOption,
} from '../../ui';
import {
  blankSprintDraft,
  validateSprintDraft,
} from '../../features/sprintboard/models/sprintboardModel';
import type { Sprint, SprintDraft } from '../../features/sprintboard/models/types';
import { useTeams } from '../../features/teams/useTeams';

export interface CreateSprintModalProps {
  open: boolean;
  onClose: () => void;
  /** Persists a new sprint's draft (room-scoped, from useSprints). */
  onCreate: (draft: SprintDraft) => Promise<Sprint>;
  /** Called after a successful create with the new sprint. */
  onCreated?: (sprint: Sprint) => void;
}

/**
 * Create Sprint — a compact modal asking only the essentials to open a sprint:
 * Name, Start / End dates, an optional Team (for capacity planning), and an
 * optional Sprint Goal. The sprint is written in 'planning' status; committing
 * tasks and reviewing capacity happen on the planning surface.
 */
export function CreateSprintModal({
  open,
  onClose,
  onCreate,
  onCreated,
}: CreateSprintModalProps) {
  const { toast } = useToast();
  const { teams } = useTeams();

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [teamId, setTeamId] = useState('');
  const [goal, setGoal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setStartDate('');
    setEndDate('');
    setTeamId('');
    setGoal('');
  }, [open]);

  const teamOptions = useMemo<SelectOption[]>(
    () => teams.map((t) => ({ value: t.id, label: t.name })),
    [teams],
  );

  const close = () => {
    if (saving) return;
    onClose();
  };

  const submit = async () => {
    const draft: SprintDraft = {
      ...blankSprintDraft(),
      name: name.trim(),
      startDate,
      endDate,
      teamId: teamId || undefined,
      goal: goal.trim() || undefined,
      status: 'planning',
    };

    const problems = validateSprintDraft(draft);
    if (problems.length > 0) {
      toast({ tone: 'error', title: 'Cannot create sprint', description: problems[0] });
      return;
    }

    setSaving(true);
    try {
      const created = await onCreate(draft);
      toast({ tone: 'success', title: 'Sprint created', description: created.name });
      onClose();
      onCreated?.(created);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Create failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      title="Create sprint"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Create sprint
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          placeholder="Sprint 24 — Checkout"
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Start date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label="End date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <Select
          label="Team"
          placeholder="No team (add later)"
          value={teamId}
          options={teamOptions}
          onChange={(e) => setTeamId(e.target.value)}
        />
        <Textarea
          label="Sprint goal"
          value={goal}
          placeholder="What this sprint is trying to achieve (optional)."
          rows={3}
          onChange={(e) => setGoal(e.target.value)}
        />
      </div>
    </Modal>
  );
}

export default CreateSprintModal;
